import { useState } from "react";
import { ChevronLeft, LogOut, PanelLeftClose, PanelLeft, X, Search } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useWorkspace } from "../../context/WorkspaceContext";
import { ContextSwitcher } from "./ContextSwitcher";
import { ChangePasswordDialog } from "./ChangePasswordDialog";
import { RefreshButton } from "../common/RefreshButton";
import { API } from "../../lib/api";
import { useBranding } from "../../lib/branding";

export function DesktopShell({ children }) {
  const { user, logout, has } = useAuth();
  const { MODULES, tabs, active, setActive, openTab, closeTab } = useWorkspace();
  const [collapsed, setCollapsed] = useState(false);
  const [moduleSearch, setModuleSearch] = useState("");
  const brand = useBranding();

  const q = moduleSearch.trim().toLowerCase();
  const visibleModules = MODULES.filter((m) => (!m.perm || has(m.perm)) &&
    (!q || m.title.toLowerCase().includes(q) || (m.group || "").toLowerCase().includes(q)));
  const groups = visibleModules.reduce((acc, m) => {
    (acc[m.group] = acc[m.group] || []).push(m);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-[#F7F8FB]">
      {/* Sidebar */}
      <aside style={{ background: "linear-gradient(180deg,#25102F 0%,#35133F 35%,#4A1653 70%,#6A1D68 100%)" }} className={`fixed left-0 top-0 bottom-0 z-40 flex flex-col text-white transition-all duration-200 ${collapsed ? "w-16" : "w-64"}`}>
        <div className="border-b border-white/10 px-3 py-4">
          <div className={`mx-auto flex items-center justify-center overflow-hidden rounded-2xl bg-white shadow-md ${collapsed ? "h-10 w-10 p-1" : "h-24 w-full p-3"}`}>
            {brand.product
              ? <img src={`${API}${brand.product}`} alt="Radiants" className="max-h-full max-w-full object-contain" />
              : <div className="grid h-full w-full place-items-center font-display text-lg font-extrabold text-[#C81E1E]">RC</div>}
          </div>
          {!collapsed && (
            <div className="mt-3 text-center">
              <div className="font-display text-xl font-extrabold tracking-wide text-[#FF9A27]">MeterFlow</div>
              <div className="mt-1 text-xs italic text-white/70">"Radiants is my Pride"</div>
            </div>
          )}
        </div>
        {!collapsed && (
          <div className="shrink-0 px-3 pt-4 pb-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-teal-300/70" />
              <input
                data-testid="module-search"
                value={moduleSearch}
                onChange={(e) => setModuleSearch(e.target.value)}
                placeholder="Search modules..."
                className="w-full rounded-md border border-white/10 bg-white/10 py-2 pl-8 pr-2 text-sm text-white placeholder:text-white/50 focus:border-white/30 focus:outline-none"
              />
            </div>
          </div>
        )}
        <nav className="flex-1 space-y-4 overflow-y-auto px-2 pb-4 pt-1">
          {Object.keys(groups).length === 0 && !collapsed && (
            <div className="px-2 text-xs text-teal-200/60" data-testid="module-search-empty">No modules match "{moduleSearch}"</div>
          )}
          {Object.entries(groups).map(([group, mods]) => (
            <div key={group}>
              {!collapsed && <div className="px-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-white/50">{group}</div>}
              <div className="space-y-1">
                {mods.map((m) => {
                  const Icon = m.icon;
                  const isActive = active === m.key;
                  return (
                    <button
                      key={m.key}
                      data-testid={`nav-${m.key}`}
                      onClick={() => openTab(m.key)}
                      style={isActive ? { background: "linear-gradient(90deg,#FF6A00 0%,#FF8A12 100%)", boxShadow: "0 4px 12px rgba(255,106,0,0.25)" } : undefined}
                      className={`flex w-full items-center gap-3 rounded-[10px] px-2.5 py-2 text-sm transition-colors ${isActive ? "font-semibold text-white" : "text-white/75 hover:bg-white/10 hover:text-white"}`}
                    >
                      <Icon className="h-4 w-4 shrink-0" />
                      {!collapsed && <span className="truncate">{m.title}</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>
        <button onClick={() => setCollapsed((c) => !c)} className="flex items-center gap-2 border-t border-white/10 px-4 py-3 text-xs text-white/70 hover:text-white">
          {collapsed ? <PanelLeft className="h-4 w-4" /> : <><PanelLeftClose className="h-4 w-4" /> Collapse</>}
        </button>
      </aside>

      {/* Top bar */}
      <header className={`fixed top-0 right-0 z-30 flex h-14 items-center justify-between border-b border-slate-200 bg-white px-4 transition-all duration-200 ${collapsed ? "left-16" : "left-64"}`}>
        <ContextSwitcher />
        <div className="flex items-center gap-3">
          <div className="hidden text-right sm:block">
            <div className="text-sm font-medium text-slate-800">{user?.full_name}</div>
            <div className="text-xs text-slate-400">{user?.is_admin ? "Global Admin" : user?.email}</div>
          </div>
          <div className="grid h-9 w-9 place-items-center rounded-full bg-[#F2ECFF] font-display text-sm font-semibold text-[#7C3AED]">
            {(user?.full_name || "U").charAt(0)}
          </div>
          <RefreshButton />
          <ChangePasswordDialog />
          <button data-testid="logout-button" onClick={logout} className="rounded-md p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-red-600" title="Log out">
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </header>

      {/* Tab bar */}
      <div className={`fixed top-14 right-0 z-20 flex h-10 items-center gap-1 overflow-x-auto border-b border-slate-200 bg-slate-100 px-2 scrollbar-none transition-all duration-200 ${collapsed ? "left-16" : "left-64"}`}>
        {tabs.map((t) => {
          const Icon = t.icon;
          const isActive = active === t.key;
          return (
            <div
              key={t.key}
              data-testid={`tab-${t.key}`}
              onClick={() => setActive(t.key)}
              className={`flex h-8 cursor-pointer items-center gap-2 rounded-t-md border-b-2 px-3 text-xs font-medium transition-colors ${isActive ? "border-[#7C3AED] bg-white text-[#6D28D9]" : "border-transparent text-slate-500 hover:bg-slate-200"}`}
            >
              <Icon className="h-3.5 w-3.5" />
              <span className="whitespace-nowrap">{t.title}</span>
              {t.key !== "dashboard" && (
                <button onClick={(e) => { e.stopPropagation(); closeTab(t.key); }} className="rounded p-0.5 hover:bg-slate-200" data-testid={`tab-close-${t.key}`}>
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Workspace */}
      <main className={`min-h-screen pt-24 transition-all duration-200 ${collapsed ? "ml-16" : "ml-64"}`}>
        <div className="p-6">{children}</div>
      </main>
    </div>
  );
}
